/**
 * Geração de proposta em PowerPoint (Clarear / LXP).
 * Usado por Calculadora Solar e Consulta de Propostas.
 */

import { isLxp, slugNomeArquivo } from './constantes'

const escapeXml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;')

/**
 * @param {{ dados: object, userEmpresa: object }} args
 * @returns {Promise<{ fileName: string, tipo: 'pptx', templateLabel: string }>}
 */
export async function gerarPropostaPPTX({ dados, userEmpresa }) {
  const lxp = isLxp(userEmpresa?.id)
  const templateFileName = lxp ? 'ModeloPropostaLXP.pptx' : 'ModeloPropostaClarear.pptx'
  const templateLabel = lxp ? 'LXP' : 'Clarear'
  const templateUrl = `${process.env.PUBLIC_URL || ''}/docs/${templateFileName}`

  const response = await fetch(templateUrl)
  if (!response.ok) throw new Error('Template não encontrado')
  const arrayBuffer = await response.arrayBuffer()

  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(arrayBuffer)

  const consumoMensal = parseFloat(dados.kwh) || 0
  const totalComMajoracao = Number(dados.totalProposta) || 0
  const valorEconomia = consumoMensal * 0.97830
  const economiaAnualR$ = Math.max(1, (Number(dados.totalComPorcentagem) || 0) * 0.15)
  const retornoAnos = totalComMajoracao / economiaAnualR$

  const substituicoes = {
    '{valorproposta}': totalComMajoracao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }),
    '{retorno}': `${retornoAnos.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Anos`,
    '{economia}': valorEconomia.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }),
    '{modulos}': `${dados.quantidadePaineis ?? ''} x ${dados.moduloPotencia ?? ''}W`,
    '{cliente}': (dados.nomeCliente || '').trim(),
    '{potencia}': `${dados.projetoKwp ?? ''}Kwp`,
    '{inversor}': `${dados.inversor ?? ''}Kw`,
    '{kwh}': `${dados.kwh ?? ''}Kwh`
  }

  const replaceInZip = (zipInstance) => {
    const promises = []
    zipInstance.forEach((relativePath, file) => {
      if (!file.dir && (relativePath.endsWith('.xml') || relativePath.endsWith('.rels'))) {
        promises.push(
          file.async('string').then((text) => {
            let novoTexto = text
            const isXml = relativePath.endsWith('.xml')
            Object.entries(substituicoes).forEach(([placeholder, valor]) => {
              const valorSafe = isXml ? escapeXml(valor) : valor
              novoTexto = novoTexto.split(placeholder).join(valorSafe)
              const key = placeholder.slice(1, -1)
              const regex = new RegExp('\\{(?:<[^>]+>|\\s)*' + key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?:<[^>]+>|\\s)*\\}', 'g')
              novoTexto = novoTexto.replace(regex, valorSafe)
            })
            zipInstance.file(relativePath, novoTexto)
          })
        )
      }
    })
    return Promise.all(promises)
  }

  await replaceInZip(zip)
  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const fileName = `${dados.fileNameBase || `Proposta_${slugNomeArquivo(dados.nomeCliente)}_${new Date().toISOString().split('T')[0]}`}.pptx`
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)

  return { fileName, tipo: 'pptx', templateLabel, templateUrl }
}
